import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { emailregex } from "../../utils/Regex";
import { addStaff } from "../../store/slices/staff";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

export default function AddStaff() {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    role: "",
    password: "",
  });

  const [permissions, setPermissions] = useState({
    package: [],
    hotel: [],
    ferry: [],
    activity: [],
    vehicle: [],
    quotation: [],
    booking: [],
    finance: [],
    reports: [],
    staff: [],
  });

  const [loading, setLoading] = useState(false);

  const permissionTypes = ["NONE", "VIEW", "EDIT", "FULL"];

  const [errors, setErrors] = useState({});
  const [permissionError, setPermissionError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "mobile") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "name") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "").slice(0, 30);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      if (name === "name") {
        if (!updatedValue.trim()) {
          newErrors.name = "Please enter name";
        } else if (updatedValue.trim().length < 3) {
          newErrors.name = "Minimum 3 characters required";
        } else {
          newErrors.name = "";
        }
      }

      if (name === "email") {
        const value = updatedValue;

        if (!value.trim()) {
          newErrors.email = "Please enter email";
        } else if (value.includes(" ")) {
          newErrors.email = "Email should not contain spaces";
        } else if (value.includes("*")) {
          newErrors.email = "Masked email is not allowed";
        } else if (!emailregex.test(value)) {
          newErrors.email = "Please enter valid email";
        } else {
          newErrors.email = "";
        }
      }

      if (name === "mobile") {
        if (!/^[6-9]\d{9}$/.test(updatedValue)) {
          newErrors.mobile = "Enter valid 10-digit mobile number";
        } else {
          newErrors.mobile = "";
        }
      }

      return newErrors;
    });
  };

  const handlePermissionChange = (module, type) => {
    setPermissions((prev) => {
      const existing = prev[module];

      let updatedPermissions;

      if (existing.includes(type)) {
        updatedPermissions = existing.filter((t) => t !== type);
      } else {
        updatedPermissions = [...existing, type];
      }

      return {
        ...prev,
        [module]: updatedPermissions,
      };
    });

    setPermissionError("");
  };

  const validate = () => {
    let err = {};

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!formData.name.trim()) {
      err.name = "Please enter name";
    } else if (formData.name.trim().length < 3) {
      err.name = "Minimum 3 characters required";
    } else if (formData.name.trim().length > 30) {
      err.name = "Maximum 30 characters allowed";
    }

    if (!/^[6-9]\d{9}$/.test(formData.mobile))
      err.mobile = "Enter valid 10-digit mobile number";

    if (!formData.email.trim()) {
      err.email = "Please enter email";
    } else if (!emailregex.test(formData.email)) {
      err.email = "Please enter valid email address";
    }

    if (!formData.role) err.role = "Select designation";

    if (!formData.password) {
      err.password = "Please enter password";
    } else if (!passwordRegex.test(formData.password)) {
      err.password =
        "Password must be 8+ chars, include uppercase, lowercase, number & special character";
    }
    // Permission validation
    const isPermissionSelected = Object.values(permissions).some(
      (module) => module.length > 0,
    );

    if (!isPermissionSelected) {
      setPermissionError("Select at least one permission");
    }

    setErrors(err);

    return Object.keys(err).length === 0 && isPermissionSelected;
  };

  const formatPermissions = (permissions) => {
    const priority = ["FULL", "EDIT", "VIEW"];

    const formatted = {};

    Object.keys(permissions).forEach((module) => {
      const selected = permissions[module];

      if (!selected || selected.length === 0) {
        formatted[module] = "NONE";
      } else {
        const highest = priority.find((p) => selected.includes(p));
        formatted[module] = highest || "NONE";
      }
    });

    return formatted;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return; // just stop here
    }

    setLoading(true);

    const payload = {
      name: formData.name,
      email: formData.email,
      mobileNumber: formData.mobile,
      designation: formData.role,
      password: formData.password,
      permissions: formatPermissions(permissions),
    };

    try {
      const response = await dispatch(addStaff(payload)).unwrap();

      toast.success(response?.message || "Staff added successfully");

      navigate("/admin/staff");
    } catch (error) {
      toast.error(error || "Failed to add staff. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>
          <Link to="/admin/staff" className="breadcrumb-link active">
            Staff
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span
            className="breadcrumb-link inactive"
            onClick={() => navigate("/admin/addstaff")}
          >
            Add Staff
          </span>
        </div>
      </div>
      <Container fluid>
        <Card className="shadow-sm my-4 border-0">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <h6 className="mb-3">Basic Information</h6>

              <Row className="g-3">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Staff Name</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter name"
                        inputMode="text"
                      />
                    </InputGroup>
                    <small className="text-danger">{errors.name}</small>
                  </Form.Group>
                </Col>

                {/* Mobile */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Mobile Number</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="Enter mobile number"
                        inputMode="numeric"
                      />
                    </InputGroup>
                    <small className="text-danger">{errors.mobile}</small>
                  </Form.Group>
                </Col>

                {/* Email */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter email"
                        maxLength={75}
                      />
                    </InputGroup>
                    <small className="text-danger">{errors.email}</small>
                  </Form.Group>
                </Col>
              </Row>

              <h6 className="mt-4 mb-3">Job Details</h6>

              <Row className="g-3">
                {/* Role */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Designation</Form.Label>
                    <InputGroup>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        <option>Manager</option>
                        <option>Receptionist</option>
                        <option>Housekeeping</option>
                        <option>Chef</option>
                      </Form.Select>
                    </InputGroup>
                    <small className="text-danger">{errors.role}</small>
                  </Form.Group>
                </Col>

                {/* Department */}
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="password"
                        maxLength={25}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter Password"
                        minLength={8}
                      />
                    </InputGroup>
                    <small className="text-danger">{errors.password}</small>
                  </Form.Group>
                </Col>
              </Row>

              <h6 className="mt-4 mb-3">Permissions</h6>

              <small style={{ color: "red" }}>{errors.checkboxerror}</small>

              <Row className="g-3">
                {Object.keys(permissions).map((module) => {
                  const modulePermissionTypes =
                    module === "reports" ? ["NONE", "VIEW"] : permissionTypes;

                  return (
                    <Col xs={12} md={4} key={module}>
                      <Card className="p-3 border-0">
                        <Form.Label className="fw-bold text-capitalize">
                          {module}
                        </Form.Label>

                        <div className="d-flex gap-3 flex-wrap">
                          {modulePermissionTypes.map((type) => (
                            <Form.Check
                              key={type}
                              type="checkbox"
                              label={type}
                              checked={permissions[module]?.includes(type)}
                              onChange={() =>
                                handlePermissionChange(module, type)
                              }
                            />
                          ))}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {permissionError && (
                <div className="text-danger mt-3 text-center">
                  {permissionError}
                </div>
              )}

              {/* Buttons */}
              <div className="text-center my-5 d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="light"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
